import { useEffect, useState } from 'react';
import { api } from '../api';

const transparent = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
const cache = new Map<string, string>();
const pending = new Map<string, Promise<string>>();

export function preloadMedia(id?: string): Promise<string> {
  if (!id) return Promise.reject(new Error('No hay imagen para cargar.'));
  const saved = cache.get(id);
  if (saved) return Promise.resolve(saved);
  const existing = pending.get(id);
  if (existing) return existing;
  const request = api<{ data: string }>(`/media/${id}`).then(async ({ data }) => {
    if (!data) throw new Error('La imagen está vacía.');
    const image = new Image();
    image.src = data;
    await new Promise<void>((resolve, reject) => {
      if (image.complete && image.naturalWidth) return resolve();
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('No pudimos cargar la imagen.'));
    });
    if (image.decode) await image.decode().catch(() => {});
    cache.set(id, data);
    return data;
  }).finally(() => pending.delete(id));
  pending.set(id, request);
  return request;
}

export function Media({ id, alt, className = '', fallback = '/assets/jerseylab-logo.png', priority = false }: {
  id?: string;
  alt: string;
  className?: string;
  fallback?: string;
  priority?: boolean;
}) {
  const [src, setSrc] = useState(() => id ? cache.get(id) || '' : fallback);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!id) {
      setSrc(fallback);
      setFailed(false);
      return;
    }
    const saved = cache.get(id);
    setSrc(saved || '');
    setFailed(false);
    if (!saved) preloadMedia(id).then(data => {
      if (alive) setSrc(data);
    }).catch(() => {
      if (alive) {
        setSrc(fallback);
        setFailed(true);
      }
    });
    return () => { alive = false; };
  }, [id, fallback]);

  return <img
    src={src || transparent}
    alt={alt}
    className={`${className} ${src ? 'media-ready' : 'media-pending'}${failed ? ' media-error' : ''}`.trim()}
    loading={priority ? 'eager' : 'lazy'}
    fetchPriority={priority ? 'high' : undefined}
    decoding="async"
  />;
}
